import axios from "axios";

/**
 * Per-user Dialer audio picks stored on the user record as
 * `dialer_preferences.<orgId>` (DynamoDB map), since recording ids are per org.
 * Missing values fall back to "nothing picked" with auto-play on.
 *
 * @param {object|null} user  User item from localStorage (DynamoDB attribute format)
 * @param {string|null|undefined} orgId  Current organization id
 * @returns {{ greeting_rec_id: string, vm_drop_rec_id: string, auto_greeting: boolean }}
 */
export function readDialerPreferences(user, orgId) {
  const m = (orgId && user?.dialer_preferences?.M?.[orgId]?.M) || {};
  return {
    greeting_rec_id: m.greeting_rec_id?.S || "",
    vm_drop_rec_id: m.vm_drop_rec_id?.S || "",
    auto_greeting: m.auto_greeting?.BOOL !== false,
  };
}

/**
 * Resolve a recording id against the org's recordings list.
 * Returns null when unset or when the recording has been deleted.
 *
 * @param {object[]} recordings  organization.recordings.L
 * @param {string} recId
 * @returns {{ recId: string, title: string, url: string } | null}
 */
export function findRecording(recordings, recId) {
  if (!recId || !Array.isArray(recordings)) return null;
  const rec = recordings.find((r) => r?.M?.rec_id?.S === recId);
  const url = rec?.M?.url?.S;
  if (!url) return null;
  return { recId, title: rec.M.title?.S || "Recording", url };
}

/**
 * Save a partial preferences update for one org and mirror the result into localStorage `user`.
 *
 * @param {string} orgId
 * @param {{ greeting_rec_id?: string, vm_drop_rec_id?: string, auto_greeting?: boolean }} patch
 */
export async function saveDialerPreferences(orgId, patch) {
  const res = await axios.post(
    process.env.REACT_APP_BACKEND_URL + "user/update-dialer-preferences",
    { organization_id: orgId, ...patch }
  );
  const dialerPreferences = res.data?.dialer_preferences;
  try {
    const stored = JSON.parse(localStorage.getItem("user") || "null");
    if (stored && dialerPreferences) {
      localStorage.setItem(
        "user",
        JSON.stringify({ ...stored, dialer_preferences: dialerPreferences })
      );
    }
  } catch (e) {
    // localStorage is a cache; the server copy is authoritative
  }
  return readDialerPreferences({ dialer_preferences: dialerPreferences }, orgId);
}
